/**
 * Risk Assessment Builder — Phase 4.
 *
 * Creates a new RA from blank or from a Template, then navigates to the
 * Detail page in draft-edit mode for the user to fill in the rest.
 *
 * Template list is fetched from the existing /api/risk-assessments/templates
 * endpoint (Phase 2). Picking a template auto-forks each referenced seed
 * block into a per-prod ra_block and snapshots all its rows into the new RA
 * (server-side, transactional).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiGet, apiPost } from '../utils/api';
import { BTN } from '../constants/buttons';
import { toLocalISO } from '../utils/dateFormat';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import ActivityField from '../components/ActivityField';
import { useDepartment } from '../contexts/DepartmentContext';
import { useAuth } from '../contexts/AuthContext';
import { FIELD_LABEL_CLASS } from '../constants/inputs';

export default function RiskAssessmentBuilder() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAdmin } = useDepartment();
  const { user } = useAuth();
  const [title, setTitle] = useState('');
  const [reference, setReference] = useState('');
  const [location, setLocation] = useState('');
  const [activity, setActivity] = useState('');
  // Default Assessor to the signed-in user's display name. Init via lazy
  // useState so we don't trigger a re-render-driven overwrite after the user
  // edits the field. If user isn't loaded yet at first render, fall back to
  // empty string; the field is editable so users can adjust either way.
  const [assessorName, setAssessorName] = useState(() => user?.display_name || '');
  const [assessmentDate, setAssessmentDate] = useState(() => toLocalISO(new Date()));
  const [reviewDate, setReviewDate] = useState('');
  const [source, setSource] = useState('blank');
  const [templates, setTemplates] = useState([]);
  const [templateKey, setTemplateKey] = useState(''); // "kind:id" e.g. "seed:1"
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [userTouchedActivity, setUserTouchedActivity] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const loadTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const res = await apiGet('/api/risk-assessments/templates');
      if (res.ok) setTemplates(await res.json());
    } catch (err) {
      console.error('[Builder] template fetch failed:', err);
    }
    setLoadingTemplates(false);
  }, []);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  // One-shot pre-fill from URL query (e.g. clicking "Use as new RA" on a
  // library template jumps here with ?title=…&source=template&kind=seed&id=5).
  // Runs once on mount only — intentionally ignoring searchParams in deps.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const qTitle = searchParams.get('title');
    if (qTitle) setTitle(qTitle);
    const qSource = searchParams.get('source');
    if (qSource === 'template') {
      setSource('template');
      const kind = searchParams.get('kind');
      const id = searchParams.get('id');
      if (kind && id) setTemplateKey(`${kind}:${id}`);
    }
  }, []);

  // Pre-fill the 4-section Activity from the selected template (URL or dropdown)
  // so users see and can edit the seeded example before clicking Create. Skips
  // once the user has manually typed in the Activity field — their edits win.
  useEffect(() => {
    if (source !== 'template' || !templateKey || userTouchedActivity) return;
    const [kind, idStr] = templateKey.split(':');
    const id = parseInt(idStr, 10);
    if (!Number.isFinite(id) || id <= 0) return;
    const path = kind === 'seed'
      ? `/api/risk-assessments/templates/seed/${id}`
      : `/api/risk-assessments/templates/fork/${id}`;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiGet(path);
        if (!res.ok || cancelled) return;
        const tpl = await res.json();
        if (cancelled) return;
        if (tpl && typeof tpl.activity === 'string' && tpl.activity.trim()) {
          setActivity(tpl.activity);
        }
      } catch (err) {
        console.warn('[Builder] template detail fetch failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [source, templateKey, userTouchedActivity]);

  const handleCreate = async () => {
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const body = {
        title: title.trim(),
        reference: reference.trim(),
        location: location.trim(),
        activity: activity.trim(),
        assessor_name: assessorName.trim(),
        assessment_date: assessmentDate || null,
        review_date: reviewDate || null,
        source,
      };
      if (source === 'template' && templateKey) {
        const [kind, id] = templateKey.split(':');
        body.source_kind = kind;
        body.template_id = parseInt(id, 10);
      }

      const res = await apiPost('/api/risk-assessments', body);
      if (!res.ok) {
        const errBody = await res.json().catch(() => null);
        throw new Error(errBody?.error || 'Failed to create RA');
      }
      const data = await res.json();
      // Pass any missing-block warnings from a template-source create down to
      // the Detail page so it can surface them once on first render.
      navigate(`/risk-assessments/${data.id}`, {
        replace: true,
        state: { missingBlocks: Array.isArray(data.missing_blocks) ? data.missing_blocks : [] },
      });
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  // Site admin cannot create RAs directly — they are not in any production
  // or department. To create on behalf of a user, admin impersonates that
  // user first via the People panel. Same pattern as NewOrder.js. The
  // conditional sits AFTER all hooks have run (per React's hooks-order rule).
  if (isAdmin) {
    return (
      <div className="px-4 sm:px-6 py-4 max-w-2xl mx-auto">
        <header className="mb-4">
          <button
            onClick={() => navigate('/risk-assessments')}
            className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-2"
          >
            ← Risk Assessments
          </button>
        </header>
        <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700 rounded-lg p-6 text-amber-900 dark:text-amber-200">
          <h2 className="text-lg font-semibold mb-2">Site admin cannot create RAs directly</h2>
          <p className="text-sm mb-3">
            Risk assessments are owned by the user who creates them, in a specific production and
            department. As site admin you are not in any production, so creating an RA from this
            account would leave it without a real owner.
          </p>
          <p className="text-sm">
            To create on behalf of a user, use <strong>Impersonate</strong> from the People panel
            to switch to that user's session — then return here to start the RA. Admin actions
            are logged with both identities.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 py-4 max-w-[96rem] mx-auto" data-mobile-inputs="">
      <header className="mb-4">
        <button
          onClick={() => navigate('/risk-assessments')}
          className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-2"
        >
          ← Risk Assessments
        </button>
        <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">New Risk Assessment</h1>
      </header>

      {error && <ErrorMessage error={error} />}

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block sm:col-span-2">
            <span className={FIELD_LABEL_CLASS}>Title *</span>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="block w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 px-2 py-1.5 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="e.g. A-Stage Wind Rig — Episode 4"
              data-testid="ra-builder-title"
              autoFocus
            />
          </label>
          <ActivityField
            value={activity}
            canEdit={true}
            onSave={(val) => { setUserTouchedActivity(true); setActivity(val); }}
          />
          <div className="grid gap-3">
            <label className="block">
              <span className={FIELD_LABEL_CLASS}>Reference</span>
              <input
                type="text"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="e.g. C-014-2"
                className="block w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 px-2 py-1.5 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </label>
            <label className="block">
              <span className={FIELD_LABEL_CLASS}>Location</span>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="e.g. A-Stage"
                className="block w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 px-2 py-1.5 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </label>
            <label className="block">
              <span className={FIELD_LABEL_CLASS}>Assessor name</span>
              <input
                type="text"
                value={assessorName}
                onChange={(e) => setAssessorName(e.target.value)}
                className="block w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 px-2 py-1.5 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className={FIELD_LABEL_CLASS}>Assessment date</span>
                <input
                  type="date"
                  value={assessmentDate}
                  onChange={(e) => setAssessmentDate(e.target.value)}
                  className="block w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 px-2 py-1.5 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </label>
              <label className="block">
                <span className={FIELD_LABEL_CLASS}>Review date</span>
                <input
                  type="date"
                  value={reviewDate}
                  onChange={(e) => setReviewDate(e.target.value)}
                  className="block w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 px-2 py-1.5 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </label>
            </div>
          </div>
        </div>

        <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
          <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Start from</p>
          <div className="grid gap-2 sm:grid-cols-2">
            <label className={`flex items-start gap-2 rounded-lg border p-3 cursor-pointer ${
              source === 'blank' ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20' : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/30'
            }`}>
              <input
                type="radio"
                name="source"
                value="blank"
                checked={source === 'blank'}
                onChange={() => setSource('blank')}
                data-testid="ra-builder-source-blank"
              />
              <div>
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">Blank</div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Start empty. Build sections row-by-row.</p>
              </div>
            </label>
            <label className={`flex items-start gap-2 rounded-lg border p-3 cursor-pointer ${
              source === 'template' ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20' : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/30'
            }`}>
              <input
                type="radio"
                name="source"
                value="template"
                checked={source === 'template'}
                onChange={() => setSource('template')}
                data-testid="ra-builder-source-template"
              />
              <div>
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">From template</div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Pre-populated with the template's blocks + hazard rows.</p>
              </div>
            </label>
          </div>

          {source === 'template' && (
            <div className="mt-3">
              {loadingTemplates ? (
                <LoadingSpinner />
              ) : (
                <select
                  value={templateKey}
                  onChange={(e) => setTemplateKey(e.target.value)}
                  className="block w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 px-2 py-1.5 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  data-testid="ra-builder-template"
                >
                  <option value="">— Select a template —</option>
                  {templates.map((t) => (
                    <option key={`${t.kind}:${t.id}`} value={`${t.kind}:${t.id}`}>
                      {t.name} ({t.block_count} block{t.block_count !== 1 ? 's' : ''})
                      {t.kind === 'seed' ? ' · CrewForge template' : ' · Custom'}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <button
          onClick={() => navigate('/risk-assessments')}
          className={BTN.secondary}
          disabled={saving}
        >
          Cancel
        </button>
        <button
          onClick={handleCreate}
          className={BTN.confirm}
          disabled={saving || !title.trim() || (source === 'template' && !templateKey)}
          data-testid="ra-builder-create"
        >
          {saving ? 'Creating…' : 'Create draft'}
        </button>
      </div>
    </div>
  );
}
